import { apiGet, apiSend } from "../../lib/api";
import { supabase } from "../../lib/supabase";
import type { VoiceCall, VoiceSettings } from "./types";

// Thin wrappers around the HQ gateway → hq-voice-worker. Auth is handled by
// `apiGet` / `apiSend` which attach the Supabase access token.

export async function fetchVoicemailStats(): Promise<{ unheard: number }> {
  const vms = await fetchVoicemails({ outcome: "voicemail" });
  return { unheard: vms.length };
}

export function fetchVoicemails(params?: {
  outcome?: string;
  limit?: number;
  phoneLine?: "line1" | "line2" | null;
}): Promise<VoiceCall[]> {
  const q = new URLSearchParams();
  if (params?.outcome) q.set("outcome", params.outcome);
  if (params?.limit) q.set("limit", String(params.limit));
  if (params?.phoneLine) q.set("phone_line", params.phoneLine);
  const qs = q.toString();
  return apiGet<VoiceCall[]>(`/voice/voicemails${qs ? `?${qs}` : ""}`);
}

export function markVoicemailListened(id: string): Promise<{ success: boolean }> {
  return apiSend(`/voice/voicemails/${encodeURIComponent(id)}/listened`, "PATCH");
}

export function hideVoicemail(id: string): Promise<{ success: boolean }> {
  return apiSend(`/voice/voicemails/${encodeURIComponent(id)}/hide`, "PATCH");
}

// GET /voice/calls — reporting-only; returns every outcome (unlike
// /voicemails which is voicemail-scoped). See Phase 4 §5c.
export function fetchCalls(params: {
  from?: string;
  to?: string;
  phoneLine?: "line1" | "line2" | null;
  limit?: number;
  offset?: number;
}): Promise<VoiceCall[]> {
  const q = new URLSearchParams();
  if (params.from) q.set("from", params.from);
  if (params.to) q.set("to", params.to);
  if (params.phoneLine) q.set("phone_line", params.phoneLine);
  if (params.limit) q.set("limit", String(params.limit));
  if (params.offset) q.set("offset", String(params.offset));
  const qs = q.toString();
  return apiGet<VoiceCall[]>(`/voice/calls${qs ? `?${qs}` : ""}`);
}

export function fetchVoiceSettings(): Promise<VoiceSettings[]> {
  return apiGet<VoiceSettings[]>("/voice/voice-settings");
}

export function saveVoiceSettings(
  id: string | number,
  patch: Partial<VoiceSettings>
): Promise<VoiceSettings> {
  return apiSend<VoiceSettings>(
    `/voice/voice-settings/${encodeURIComponent(String(id))}`,
    "PUT",
    patch
  );
}

export type CreateVoiceLineInput = {
  phone_line: string;
  forward_to_number: string;
};

// POST /voice-settings — admin-only. Replaces the `insert into
// voice.voice_settings ...` SQL that was the only way to provision a new
// line pre-5b-cleanup.
export function createVoiceLine(input: CreateVoiceLineInput): Promise<VoiceSettings> {
  return apiSend<VoiceSettings>("/voice/voice-settings", "POST", input);
}

// ── FAQ API ────────────────────────────────────────────────────────────

export type VoiceFaq = {
  id: number;
  category: string;
  trigger_phrases: string[];
  answer_text: string;
  sms_url: string | null;
  audio_key: string | null;
  priority: number;
  enabled: boolean;
  created_at: string;
  updated_at: string;
};

export type FaqInput = {
  category: string;
  trigger_phrases: string[];
  answer_text: string;
  sms_url?: string | null;
  priority?: number;
  enabled?: boolean;
};

export function fetchFaqs(): Promise<VoiceFaq[]> {
  return apiGet<VoiceFaq[]>("/voice/faqs");
}

export function createFaq(input: FaqInput): Promise<VoiceFaq> {
  return apiSend<VoiceFaq>("/voice/faqs", "POST", input);
}

export function updateFaq(id: number, patch: Partial<FaqInput>): Promise<{ ok: boolean }> {
  return apiSend<{ ok: boolean }>(`/voice/faqs/${id}`, "PATCH", patch);
}

export function deleteFaq(id: number): Promise<{ ok: boolean }> {
  return apiSend<{ ok: boolean }>(`/voice/faqs/${id}`, "DELETE");
}

export function regenerateFaqAudio(): Promise<{ queued: number; message: string }> {
  return apiSend<{ queued: number; message: string }>("/voice/faqs/regenerate-audio", "POST");
}

// Authed audio fetch. Returns an object URL the <audio> tag can point at.
// The worker streams the bytes with the caller's JWT — the raw Twilio URL
// never reaches the browser, closing the audit-finding 2.5 hole.
//
// We could fetch streaming via the element's `src` + a custom header, but the
// Web Audio / HTML5 <audio> element can't carry an Authorization header.
// Blobbing the whole recording works fine for sub-minute voicemails.
export async function fetchVoicemailAudioUrl(id: string): Promise<string> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error("no_session");

  const apiUrl = import.meta.env.VITE_HQ_API_URL ?? "https://api.hq.tribalaxe.com";
  const r = await fetch(`${apiUrl}/voice/voicemails/${encodeURIComponent(id)}/audio`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!r.ok) throw new Error(`${r.status}: ${await r.text().catch(() => "")}`);
  const blob = await r.blob();
  return URL.createObjectURL(blob);
}
